/**
 * `GET /job-apply/api/cv/score` — la nota de la hoja de vida con la que se aplica.
 *
 * Es el único GET del servicio que puede llamar al modelo, y lo hace una sola vez
 * por hoja de vida: los CVs anteriores al puntaje se calculan aquí, a demanda, y
 * quedan guardados. La segunda lectura ya es instantánea.
 *
 * Un estado y no un `throw`, por lo mismo que el cliente del screening: estas
 * respuestas se cuentan distinto. `no_cv` no es un error —es alguien que todavía
 * no subió nada y a quien hay que ofrecerle subirlo— y `503` tampoco: el modelo
 * no contestó, reintentar es exactamente lo correcto y decir "algo falló" ahí
 * sería echarle la culpa a la persona.
 */
import { apiFetch } from '~/lib/api';
import { messages } from '~/i18n';
import { normalizeScoreDetail, type CVScoreDetail } from '../lib/cvScore';

export type CVScoreState =
  /** `hasProfile`: hay hoja de vida por piezas (lo suyo se mejora en Mis datos, no en el cuestionario). */
  | { status: 'ok'; cvId: string; computed: boolean; detail: CVScoreDetail; hasProfile?: boolean }
  /** Todavía no hay hoja de vida. La pantalla ofrece crearla, no un error. */
  | { status: 'no_cv' }
  /** El modelo no respondió. Se reintenta. */
  | { status: 'unavailable' }
  | { status: 'error'; message: string };

export async function fetchCVScore(): Promise<CVScoreState> {
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/cv/score');
  } catch {
    return { status: 'error', message: messages().onboarding.client.network };
  }

  if (res.status === 404) return { status: 'no_cv' };
  if (res.status === 503) return { status: 'unavailable' };

  const body = (await res.json().catch(() => null)) as Record<string, any> | null;

  if (!res.ok || !body) {
    if (body?.code === 'no_cv') return { status: 'no_cv' };
    if (body?.code === 'scoring_unavailable') return { status: 'unavailable' };
    return {
      status: 'error',
      message: body?.error ?? messages().onboarding.cvScore.readFailed,
    };
  }

  const detail = normalizeScoreDetail(body);
  // Un 200 sin puntaje adentro no existe en el contrato, pero si llegara sería
  // "no calculado" y NO un cero: se trata como el modelo caído.
  if (!detail) return { status: 'unavailable' };

  return {
    status: 'ok',
    cvId: String(body.cv_id ?? ''),
    computed: body.computed === true,
    detail,
    hasProfile: body.has_profile === true,
  };
}
